<template lang="pug">
  .ui-form-field
    //- Field base -------------------------------------------------------------
    .ui-field-base(:class="fieldBaseClasses")
      //- Masked input ---------------------------------------------------------
      the-mask.ui-input(
      :type="type"
      :value="value"
      :id="inputID"
      :mask="mask"
      :tokens="maskTokens"
      @input="onInput"
      @focus.native="onFocus"
      @blur.native="onBlur"
      :readonly="isDisabled"
      :disabled="isDisabled"
      v-if="isMasked"
      )
      //- Default input --------------------------------------------------------
      input.ui-input(
      :type="inputType"
      :value="value"
      :id="inputID"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
      :readonly="isDisabled"
      :disabled="isDisabled"
      v-else
      )
      //- Label ----------------------------------------------------------------
      label.ui-label.rpc_text-clip(:for="inputID")
        span.label-text(v-if="!centered" v-text="label")
        span.error(v-if="hasError" v-text="error")

    //- Field actions ----------------------------------------------------------
    .ui-field-actions
      //- Field button ---------------------------------------------------------
      button.ui-field-btn.action(
      v-if="fieldBtn"
      type="button"
      :class="fieldBtnClasses"
      v-text="fieldBtnText"
      :disabled="fieldBtnIsDisabled"
      @click="fieldBtnClick"
      )
      //- Field toggle ---------------------------------------------------------
      button.ui-field-toggle.action(
      v-if="hasToggle"
      tabindex="-1"
      type="button"
      @click="toggleInputType"
      ): svg: use(:xlink:href="require('@/assets/img/ui-graphics.svg') + '#ui-toggle-' + toggleIcon")
      //- Field slot -----------------------------------------------------------
      .ui-field-slot.action(v-if="hasSlot"): slot

</template>

<script>
import { TheMask } from 'vue-the-mask' // Masked input component

export default {
  name: 'UiFormField',
  inheritAttrs: false, // Prevent directional inheritance for attributes

  // Components ----------------------------------------------------------------
  components: { TheMask },

  // VeeValidate ---------------------------------------------------------------
  $_veeValidate: {
    name () {
      return this.name
    },
    value () {
      return this.value
    }
  },

  // Props ---------------------------------------------------------------------
  props: {

    // Input value (used for v-model)
    value: {
      type: String,
      default: ''
    },

    // Input name (must be unique)
    name: {
      type: String,
      required: true
    },

    // Input type (one of available types)
    type: {
      type: String,
      default: 'text',
      validator (value) {
        return ['text', 'email', 'password', 'search', 'tel'].indexOf(value) !== -1
      }
    },

    // Label text
    label: {
      type: String,
      default: 'Empty'
    },

    // Is input disabled
    isDisabled: {
      type: Boolean,
      default: false
    },

    // Toggle
    toggle: {
      type: Boolean,
      default: false
    },

    // Centered
    centered: {
      type: Boolean,
      default: false
    },

    // Field button
    fieldBtn: {
      type: Boolean,
      default: false
    },

    // Field button text
    fieldBtnText: {
      type: String,
      default: 'Button'
    },

    // Field button disabled state
    fieldBtnIsDisabled: {
      type: Boolean,
      default: false
    },

    // Field button error mode
    fieldBtnError: {
      type: Boolean,
      default: false
    },

    // Error from VeeValidate
    error: {
      type: String,
      default: ''
    },

    // Mask
    mask: {
      type: String,
      default: ''
    }

  },

  // Data ----------------------------------------------------------------------
  data () {
    return {
      inputHasFocus: false, // Default field focus state
      maskTokens: {
        // Docs here: [https://vuejs-tips.github.io/vue-the-mask/]
        '#': { pattern: /\d/ }, // all numbers
        '!': { escape: true } // use this for prefix creation
      },
      curInputType: 'password' // start point for toggle feature
    }
  },

  // Computed ------------------------------------------------------------------
  computed: {

    // Has toggle (for input type 'password' only)
    hasToggle () {
      return this.type === 'password' && this.toggle
    },

    // Input type
    inputType () {
      // Check if has toggle feature is enabled  or not
      return this.hasToggle ? this.curInputType : this.type
    },

    // Toggle icon
    toggleIcon () {
      return this.curInputType === 'password' ? 'show' : 'hide'
    },

    // Use masked input or not
    isMasked () {
      // We can use masked input if
      return !!this.mask && (this.type === 'text' || this.type === 'tel')
    },

    // Has slot (detect from $slots)
    hasSlot () {
      return !!this.$slots.default
    },

    // Input ID (connection of label and input)
    inputID () {
      return 'id-' + this.name // if name == 'input' => id="id-input"
    },

    // Has error
    hasError () {
      return !!this.error // from props
    },

    // Field base classes
    fieldBaseClasses () {
      return {
        'has-focus': this.inputHasFocus, // from data
        'has-value': !!this.value, // from props
        'has-error': this.hasError, // from computed
        centered: this.centered, // from props
        disabled: this.isDisabled // from props
      }
    },

    // Field button classes
    fieldBtnClasses () {
      return {
        'error-shake': this.fieldBtnError,
        disabled: this.fieldBtnIsDisabled
      }
    }
  },

  // Methods -------------------------------------------------------------------
  methods: {

    // Field button click handler
    fieldBtnClick () {
      if (this.fieldBtnIsDisabled) return // prevent event handler if disabled
      this.eventEmitter('field-btn-click')
    },

    // Toggle input type
    toggleInputType () {
      // This handler can be called only if hasToggle return true, we don't
      // need check twice.
      this.curInputType === 'password'
        ? this.curInputType = 'text'
        : this.curInputType = 'password'
    },

    // Input 'onFocus' event handler
    onFocus () {
      if (!this.isDisabled) this.inputHasFocus = true // prevent if disabled
    },

    // Input 'onBlur' event handler
    onBlur () {
      if (!this.isDisabled) this.inputHasFocus = false // prevent if disabled
    },

    // On input event emitter (v-model deep data binding)
    onInput (mixed) {
      if (this.isDisabled) return // prevent input events if disabled
      mixed.target
        ? this.eventEmitter('input', mixed.target.value)
        : this.eventEmitter('input', mixed)
    },

    // Event emitter
    eventEmitter (eventName, eventValue) {
      eventValue || eventValue === '' // => undefined fix
        ? this.$emit(eventName, eventValue) // send event with value
        : this.$emit(eventName) // send event only
    }
  }
}
</script>
